import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { GeoBrowseGeneStudy } from '../../types';
import { useGeoBrowseContext } from '../../context/GeoBrowseContext';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import { fetchGeneStudySummary, fetchRecurrentGenes } from '../../services/geoBrowseApi';
import {
  formatDiseaseName,
  formatLog2Fc,
  formatNumber,
  formatPadj,
} from '../../utils/formatters';
import { parseGenesParam } from '../../utils/geneSelection';
import { buildStudyDetailPath, comparisonRowKey } from '../../utils/studyHelpers';
import BrowseDataTable, { type BrowseColumn } from '../BrowseDataTable';
import PageHeader from '../PageHeader';
import GeneChipList from './GeneChipList';
import GeneEffectForestPlot from './GeneEffectForestPlot';
import styles from '../../GeoBrowse.module.css';

type GeneDetailTab = 'forest' | 'table';

const MAX_SELECTED = 12;
const MAX_VIEWING = 2;

type TableRow = GeoBrowseGeneStudy & { gene_symbol: string };

function parseViewParam(value: string | null, selected: string[]): string[] {
  if (!selected.length) return [];

  const fromParam = (value ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(symbol => selected.includes(symbol))
    .slice(0, MAX_VIEWING);

  if (fromParam.length) return fromParam;
  return selected.slice(0, Math.min(MAX_VIEWING, selected.length));
}

export default function GeneDetailsBrowseView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { buildPathWithThresholds } = useGeoBrowseContext();
  const [activeTab, setActiveTab] = useState<GeneDetailTab>('forest');
  const [studyData, setStudyData] = useState<Record<string, GeoBrowseGeneStudy[]>>({});
  const [studiesLoading, setStudiesLoading] = useState(false);
  const [studiesError, setStudiesError] = useState<string | null>(null);

  const { data: recurrentGenes, loading: genesLoading, error: genesError } = useGeoBrowseQuery(
    () => fetchRecurrentGenes(),
    [],
  );

  const selectedGenes = useMemo(() => parseGenesParam(searchParams.get('genes')), [searchParams]);

  const viewingGenes = useMemo(
    () => parseViewParam(searchParams.get('view'), selectedGenes),
    [searchParams, selectedGenes],
  );

  const updateSearchParams = useCallback(
    (genes: string[], view: string[]) => {
      const next = new URLSearchParams(searchParams);
      if (genes.length) {
        next.set('genes', genes.join(','));
      } else {
        next.delete('genes');
        next.delete('view');
      }
      if (genes.length && view.length) {
        next.set('view', view.join(','));
      } else if (genes.length) {
        next.delete('view');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setSelectedGenes = useCallback(
    (symbols: string[]) => {
      const nextView = parseViewParam(searchParams.get('view'), symbols);
      updateSearchParams(symbols, nextView);
    },
    [searchParams, updateSearchParams],
  );

  const setViewingGenes = useCallback(
    (symbols: string[]) => {
      const nextView = symbols.slice(0, MAX_VIEWING).filter(s => selectedGenes.includes(s));
      if (!nextView.length && selectedGenes.length) {
        updateSearchParams(selectedGenes, selectedGenes.slice(0, 1));
        return;
      }
      updateSearchParams(selectedGenes, nextView);
    },
    [selectedGenes, updateSearchParams],
  );

  useEffect(() => {
    if (!selectedGenes.length) {
      setStudyData({});
      setStudiesLoading(false);
      setStudiesError(null);
      return undefined;
    }

    let cancelled = false;
    setStudiesLoading(true);
    setStudiesError(null);

    Promise.all(selectedGenes.map(symbol => fetchGeneStudySummary(symbol)))
      .then(results => {
        if (cancelled) return;
        const next: Record<string, GeoBrowseGeneStudy[]> = {};
        selectedGenes.forEach((symbol, index) => {
          next[symbol] = results[index] ?? [];
        });
        setStudyData(next);
      })
      .catch(err => {
        if (!cancelled) {
          setStudiesError(err instanceof Error ? err.message : 'Failed to load gene studies');
        }
      })
      .finally(() => {
        if (!cancelled) setStudiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedGenes]);

  const viewingPanels = useMemo(
    () =>
      viewingGenes.map(geneSymbol => ({
        geneSymbol,
        rows: studyData[geneSymbol] ?? [],
      })),
    [viewingGenes, studyData],
  );

  const tableRows = useMemo<TableRow[]>(
    () =>
      selectedGenes.flatMap(symbol =>
        (studyData[symbol] ?? []).map(row => ({ ...row, gene_symbol: symbol })),
      ),
    [selectedGenes, studyData],
  );

  const columns: BrowseColumn<TableRow>[] = useMemo(
    () => [
      { id: 'gene_symbol', label: 'gene_symbol', accessor: r => r.gene_symbol },
      {
        id: 'disease',
        label: 'cui_name',
        sortValue: r => r.cui_name,
        accessor: r => formatDiseaseName(r.cui_name),
      },
      { id: 'accession', label: 'accession', accessor: r => r.accession },
      { id: 'comparison_id', label: 'comparison_id', accessor: r => r.comparison_id },
      {
        id: 'log2fc',
        label: 'log2FoldChange',
        sortValue: r => r.log2FoldChange,
        accessor: r => formatLog2Fc(r.log2FoldChange),
      },
      { id: 'lfcSE', label: 'lfcSE', sortValue: r => r.lfcSE, accessor: r => r.lfcSE.toFixed(3) },
      { id: 'padj', label: 'padj', sortValue: r => r.padj, accessor: r => formatPadj(r.padj) },
      {
        id: 'significant',
        label: 'significant',
        sortValue: r => (r.significant ? 1 : 0),
        accessor: r => String(r.significant),
      },
      {
        id: 'baseMean',
        label: 'baseMean',
        sortValue: r => r.baseMean,
        accessor: r => formatNumber(Math.round(r.baseMean)),
      },
    ],
    [],
  );

  const addGene = (symbol: string) => {
    if (selectedGenes.includes(symbol) || selectedGenes.length >= MAX_SELECTED) return;
    setSelectedGenes([...selectedGenes, symbol]);
  };

  const removeGene = (symbol: string) => {
    setSelectedGenes(selectedGenes.filter(g => g !== symbol));
  };

  if (genesLoading) return <div className={styles.loading}>Loading recurrent genes…</div>;
  if (genesError) return <div className={styles.error}>{genesError}</div>;

  return (
    <>
      <PageHeader
        title="Gene Details"
        description="Select genes from the Genes table or the picker below to compare effect sizes across studies."
      />

      <div className={styles.geneSelectorBlock}>
        <label className={styles.geneSelectorLabel} htmlFor="gene-details-picker">
          Gene(s) — pick from Genes table or add a recurrent gene below
          {selectedGenes.length >= MAX_SELECTED && (
            <span className={styles.geneSelectorLimit}> — max {MAX_SELECTED} genes</span>
          )}
        </label>
        <div className={styles.geneSelectorRow}>
          <select
            id="gene-details-picker"
            className={styles.geneSelectorSelect}
            value=""
            onChange={e => {
              if (e.target.value) addGene(e.target.value);
            }}
            disabled={selectedGenes.length >= MAX_SELECTED}
          >
            <option value="">Add gene…</option>
            {(recurrentGenes ?? [])
              .filter(g => !selectedGenes.includes(g.gene_symbol))
              .map(g => (
                <option key={g.gene_symbol} value={g.gene_symbol}>
                  {g.gene_symbol} ({g.n_studies} studies)
                </option>
              ))}
          </select>

          <GeneChipList
            selectedGenes={selectedGenes}
            recurrentGenes={recurrentGenes ?? []}
            onRemove={removeGene}
          />
        </div>
      </div>

      {!selectedGenes.length ? (
        <div className={styles.empty}>
          No genes selected yet. Click rows in the Genes table to build a selection, or add a gene
          from the picker above.
        </div>
      ) : (
        <div className={styles.mainPanel}>
          <div className={styles.tabBar}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'forest' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('forest')}
            >
              Effect size across studies
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'table' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('table')}
            >
              Table
              {selectedGenes.length > 1 && (
                <span className={styles.tabBadge}>{selectedGenes.length} genes</span>
              )}
            </button>
          </div>

          {studiesLoading && <div className={styles.tabLoading}>Loading study summaries…</div>}
          {studiesError && <div className={styles.error}>{studiesError}</div>}

          {!studiesLoading && !studiesError && activeTab === 'forest' && (
            <GeneEffectForestPlot
              panels={viewingPanels}
              allSelectedGenes={selectedGenes}
              viewingGenes={viewingGenes}
              onViewingGenesChange={setViewingGenes}
            />
          )}

          {!studiesLoading && !studiesError && activeTab === 'table' && (
            <div className={styles.tabPanel}>
              {!tableRows.length ? (
                <div className={styles.tabEmpty}>No study rows for the selected genes.</div>
              ) : (
                <BrowseDataTable
                  rows={tableRows}
                  columns={columns}
                  rowKey={r => comparisonRowKey(r, r.gene_symbol)}
                  searchPlaceholder="Search gene, disease or accession…"
                  searchFilter={(row, q) =>
                    `${row.gene_symbol} ${row.cui_name} ${row.accession} ${row.comparison_id}`.toLowerCase().includes(q)
                  }
                  onRowClick={row =>
                    navigate(
                      buildPathWithThresholds(
                        buildStudyDetailPath(row.cui, row.accession, row.comparison_id),
                      ),
                    )
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
